import type { ComponentType, ReactNode } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type SideNavItem = {
  id: string;
  label: ReactNode;
  href: string;
  icon?: ComponentType<IconProps>;
  /** A count or status, formatted. */ badge?: ReactNode;
};

export type SideNavProps = {
  items: SideNavItem[];
  /** The current page's id. */
  value: string;
  /** Accessible name of the navigation ("Account"). */
  label: string;
  className?: string;
};

/**
 * Section navigation for settings, the profile and CV Studio, and the employer portal: a vertical
 * list from md (the current page in ink), a sideways-scrolling row on phones.
 */
export function SideNav({ items, value, label, className }: SideNavProps) {
  return (
    <nav aria-label={label} className={className}>
      <ul className="flex gap-1 overflow-x-auto md:flex-col md:overflow-visible">
        {items.map((item) => {
          const current = item.id === value;
          return (
            <li key={item.id} className="shrink-0">
              <a
                href={item.href}
                aria-current={current ? 'page' : undefined}
                className={cx(
                  'flex h-10 items-center gap-2.5 rounded-jc-control px-4 text-sm font-semibold whitespace-nowrap',
                  'transition-colors duration-(--jc-duration-fast) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus',
                  current
                    ? 'bg-jc-action text-jc-on-action'
                    : 'text-jc-ink-secondary hover:bg-jc-elevated hover:text-jc-ink',
                )}
              >
                {item.icon ? <Icon icon={item.icon} size="sm" /> : null}
                <span className="flex-1">{item.label}</span>
                {item.badge != null ? <span className="text-xs tabular-nums opacity-80">{item.badge}</span> : null}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
